import { UserJourneyAction, UserJourneyState } from './types'

export const userJourneyReducer = (state: UserJourneyState, action: UserJourneyAction): UserJourneyState => {
  const journeyExists = action.name in state.journeys

  switch (action.type) {
    case 'USER_JOURNEY_START': {
      const newJourney = {
        id: action.id,
        name: action.name,
        created: new Date().getTime(),
        variant: undefined,
        frustrations: [],
        frictions: [],
        tags: [],
      }

      const newHistory = state.history.length > 0 ? [...state.history.filter((journey) => journey !== newJourney.name), newJourney.name] : [newJourney.name]

      return {
        history: newHistory,
        active: action.name,
        journeys: {
          ...state.journeys,
          [action.name]: newJourney,
        },
      }
    }
    case 'USER_JOURNEY_RESUME': {
      // Never clobber a live journey — resume only fills the gap left by a page load
      if (journeyExists) {
        return state
      }

      return {
        // Same defensive dedup as START, in case history and journeys ever disagree
        history: [...state.history.filter((journey) => journey !== action.journey.name), action.journey.name],
        active: action.journey.name,
        journeys: {
          ...state.journeys,
          [action.journey.name]: action.journey,
        },
      }
    }
    case 'USER_JOURNEY_COMPLETE': {
      if (!journeyExists) {
        return state
      }

      // history is the full stack of in-flight journeys with the active one last, so
      // completing any journey is a plain removal; the last remaining name (if any)
      // is the active journey. The cash-web original popped the reactivated journey
      // out of history here, which orphaned it once another journey started.
      const newHistory = state.history.filter((journey) => journey !== action.name)
      const newJourneys = { ...state.journeys }
      delete newJourneys[action.name]

      return {
        ...state,
        history: newHistory,
        active: newHistory.length > 0 ? newHistory[newHistory.length - 1] : undefined,
        journeys: newJourneys,
      }
    }
    case 'USER_JOURNEY_ADD_FRUSTRATION': {
      if (!journeyExists) {
        return state
      }

      return {
        ...state,
        journeys: {
          ...state.journeys,
          [action.name]: {
            ...state.journeys[action.name],
            frustrations: [...state.journeys[action.name].frustrations, action.frustration],
          },
        },
      }
    }
    case 'USER_JOURNEY_ADD_FRICTION': {
      if (!journeyExists) {
        return state
      }
      return {
        ...state,
        journeys: {
          ...state.journeys,
          [action.name]: {
            ...state.journeys[action.name],
            frictions: [...state.journeys[action.name].frictions, action.friction],
          },
        },
      }
    }
    case 'USER_JOURNEY_ADD_TAG': {
      if (!journeyExists) {
        return state
      }
      return {
        ...state,
        journeys: {
          ...state.journeys,
          [action.name]: { ...state.journeys[action.name], tags: [...state.journeys[action.name].tags, action.tag] },
        },
      }
    }
    case 'USER_JOURNEY_SET_VARIANT': {
      if (!journeyExists) {
        return state
      }
      return {
        ...state,
        journeys: {
          ...state.journeys,
          [action.name]: {
            ...state.journeys[action.name],
            variant: action.variant,
          },
        },
      }
    }

    default:
      return state
  }
}
